import { css } from "@emotion/css";
import styled from "@emotion/styled";
import { Button, Colors, DropdownInput, Option, Checkbox, Fonts } from "@snapchat/snap-design-system";
import React, { type FC, useEffect, useMemo, useState, useCallback } from "react";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { DropdownWithCreateNew } from "../../common/components/DropdownWithCreateNew";
import { ContainerWithCamkitIcon } from "../../common/containers/onboarding/ContainerWithCamkitIcon";
import { Visible } from "../../common/components";
import { Flexbox } from "../../core/components";
import { useModal } from "../../core/hooks";
import { useOrganizations, useSelectedOrganization } from "../../organizations/hooks";
import { type RouteParams, AbsoluteRoute } from "../../../core/routes";
import { NewAppModal } from "../components/NewAppModal";
import { UnauthorizedMessage } from "../components/UnauthorizedMessage";
import { NewVersionModal } from "../components/NewVersionModal";
import { useApps } from "../hooks/useApps";
// Aliases-shim types kept because orgAuthorizedOperations comes from
// useSelectedOrganization which still surfaces proto-typed data at its
// boundary (the hook casts service responses to the alias types so
// un-migrated camkit features don't see drift).
import { AuthOperation, AuthOperationTypes } from "../../gql-generated";
import { getAppInfo } from "../app-details/api/query";

export const NewAppContainer: FC = () => {
    const navigate = useNavigate();
    const { orgId } = useParams<RouteParams[AbsoluteRoute.ORG_APPS_CREATE]>();
    const { loading: isFetchingOrgs } = useOrganizations();
    const { orgAuthorizedOperations, isNotSelected: orgNotSelected } = useSelectedOrganization();

    const [selectedApp, setSelectedApp] = useState<string | undefined>(undefined);
    const [selectedVersion, setSelectedVersion] = useState<number | undefined>(undefined);
    const [error, setError] = useState<string>();
    const [acknowledgedGuidelines, setAcknowledgedGuidelines] = useState(false);
    const [acknowledgedTerms, setAcknowledgedTerms] = useState(false);

    const { apps, enableCamKit, isCreatingApp, isCreatingVersion, isEnabling } = useApps({});

    useEffect(() => {
        setSelectedApp(undefined);
    }, [orgId]);

    const activeApp = useMemo(() => apps.find((app) => app.appId === selectedApp), [apps, selectedApp]);
    const orgAppAuth = useMemo(
        () =>
            orgAuthorizedOperations?.find((op) => op.authOperation === AuthOperation.AuthOperationSnapKitApps)
                ?.authOperationTypes || [],
        [orgAuthorizedOperations],
    );
    const appAuth = activeApp?.authorizedOperations ?? [];

    const hasCreateAndUpdateAuth = useCallback((ops: readonly (string | null)[]): boolean => {
        const createAuth = ops.includes(AuthOperationTypes.Create);
        const updateAuth = ops.includes(AuthOperationTypes.Update);

        return createAuth && updateAuth;
    }, []);

    const canEnableSelectedApp = hasCreateAndUpdateAuth(appAuth);
    const canCreateAppsForOrg = hasCreateAndUpdateAuth(orgAppAuth);

    const versions = useMemo(() => activeApp?.versions ?? [], [activeApp]);
    const newestVersion = useMemo(() => {
        const newestVersion = versions.reduce(
            (prevNewest, currVersion) => Math.max(prevNewest, currVersion.version ?? -1),
            -1,
        );
        return newestVersion === -1 ? undefined : newestVersion;
    }, [versions]);

    useEffect(() => {
        setSelectedVersion(newestVersion);
    }, [newestVersion]);

    const [appModalProps, showAppModal] = useModal();
    const [versionModalProps, showVersionModal] = useModal();

    const handleBack = () =>
        navigate(
            generatePath(AbsoluteRoute.ORG_APPS, {
                orgId: orgId ?? null,
            }),
        );

    const handleNewApp = (appId: string) => {
        setSelectedApp(appId);
        appModalProps.onCancel();
    };

    const handleNewVersion = (version: number) => {
        setSelectedVersion(version);
        versionModalProps.onCancel();
    };

    const handleSubmit = async () => {
        setError(undefined);
        if (selectedApp && selectedVersion !== undefined) {
            try {
                const { errors } = await enableCamKit({
                    variables: {
                        appId: selectedApp,
                        sourceVersion: selectedVersion,
                    },
                    refetchQueries: [
                        {
                            query: getAppInfo,
                            variables: {
                                appId: selectedApp,
                                orgId,
                            },
                        },
                    ],
                });

                if (errors) {
                    console.error(errors);
                    throw new Error("Failed to enable camera kit");
                }

                navigate(
                    generatePath(AbsoluteRoute.ORG_APP, {
                        orgId: orgId ?? null,
                        appId: selectedApp ?? null,
                    }),
                );
            } catch (e) {
                if (e instanceof Error) {
                    setError(e.message);
                    throw new Error(e.message);
                } else {
                    throw e;
                }
            }
        } else {
            throw new Error("Failed to enable camera kit");
        }
    };

    return (
        <ContainerWithCamkitIcon>
            <Title>Select your App and Version to Enable Camera Kit</Title>
            <DropdownInput
                disabled={isEnabling || isFetchingOrgs}
                className={dropdownStyle}
                loading={isCreatingApp || isFetchingOrgs}
                value={selectedApp}
                placeholder="Select App..."
                data-testid="select-app"
                onChange={setSelectedApp}
                dropdownRender={(menuNode: React.ReactNode) => (
                    <DropdownWithCreateNew
                        menuNode={menuNode}
                        text="Create App"
                        showModal={showAppModal}
                        showCreateButton={canCreateAppsForOrg}
                        data-testid="create-app"
                    />
                )}
            >
                {apps.map(({ appId, name }) => {
                    return (
                        <Option value={appId} key={appId}>
                            {name}
                        </Option>
                    );
                })}
            </DropdownInput>
            <DropdownInput
                disabled={isEnabling || !selectedApp || isFetchingOrgs || !canEnableSelectedApp}
                className={dropdownStyle}
                loading={isCreatingVersion || isFetchingOrgs}
                value={selectedVersion}
                data-testid="select-version"
                placeholder="Choose a version..."
                onChange={setSelectedVersion}
                dropdownRender={(menuNode: React.ReactNode) => (
                    <DropdownWithCreateNew
                        menuNode={menuNode}
                        text="Create Version"
                        showModal={showVersionModal}
                        showCreateButton={canEnableSelectedApp}
                        data-testid="create-version"
                    />
                )}
            >
                {versions.map(({ version, versionName }) => (
                    <Option value={version} key={version}>
                        {Boolean(versionName?.trim()) ? versionName : version}
                    </Option>
                ))}
            </DropdownInput>
            <div className={dropdownStyle}>
                <Checkbox
                    label={
                        <Required>
                            I have read the{" "}
                            <a
                                target="_blank"
                                rel="noopener noreferrer"
                                href="https://developers.snap.com/snap-kit/app-review/brand-guidelines#snap-product-imitation"
                            >
                                Snap Kit Developer Guidelines
                            </a>
                            , and confirm that I will not build any products or services that duplicate, or are
                            confusingly similar to features or functionality within Snapchat.
                        </Required>
                    }
                    value={acknowledgedGuidelines}
                    onChange={(value) => {
                        setAcknowledgedGuidelines(value);
                    }}
                />
                <Checkbox
                    label={
                        <Required>
                            By enabling Camera Kit on my app, I acknowledge that the{" "}
                            <a
                                target="_blank"
                                rel="noopener noreferrer"
                                href="https://snap.com/en-US/terms/snap-camera-kit"
                            >
                                Snap Camera Kit Terms
                            </a>{" "}
                            and{" "}
                            <a target="_blank" rel="noopener noreferrer" href="https://snap.com/en-US/terms/developer">
                                Snap Developer Terms
                            </a>{" "}
                            will apply.
                        </Required>
                    }
                    value={acknowledgedTerms}
                    onChange={(value) => {
                        setAcknowledgedTerms(value);
                    }}
                />
            </div>
            <ButtonWrapper>
                <Button
                    isDisabled={isCreatingApp || isCreatingVersion || isFetchingOrgs}
                    onClick={handleBack}
                    data-testid="back-button"
                >
                    Back
                </Button>
                <Button
                    type="primary"
                    isDisabled={
                        isCreatingApp ||
                        isCreatingVersion ||
                        isFetchingOrgs ||
                        isEnabling ||
                        !selectedApp ||
                        !canEnableSelectedApp ||
                        !acknowledgedGuidelines ||
                        !acknowledgedTerms
                    }
                    isPending={isEnabling}
                    onClick={handleSubmit}
                    data-testid="enable-button"
                >
                    Enable
                </Button>
            </ButtonWrapper>
            <ErrorContainer>
                <Visible when={Boolean(error)}>
                    <ErrorMessage>There was an error submitting your request: {error}</ErrorMessage>
                </Visible>
                <Visible when={!orgNotSelected && !canCreateAppsForOrg}>
                    <ErrorMessage className={authErrorCSS}>
                        *You need the Organization Admin or Business Admin role to create an app.
                    </ErrorMessage>
                </Visible>
                <Visible when={Boolean(selectedApp && !canEnableSelectedApp)}>
                    <ErrorMessage className={authErrorCSS}>
                        *You need the Snap Kit Project Admin role to enable Camera Kit for this app.
                    </ErrorMessage>
                </Visible>
                <Visible
                    when={Boolean((!orgNotSelected && !canCreateAppsForOrg) || (selectedApp && !canEnableSelectedApp))}
                >
                    <ErrorMessage className={viewRoleCss}>
                        <UnauthorizedMessage organizationId={orgId ?? ""} />
                    </ErrorMessage>
                </Visible>
            </ErrorContainer>
            <NewAppModal
                visible={appModalProps.visible}
                onCancel={appModalProps.onCancel}
                handleNewApp={handleNewApp}
            />
            <NewVersionModal
                visible={versionModalProps.visible}
                onCancel={versionModalProps.onCancel}
                handleNewVersion={handleNewVersion}
                existingVersions={versions}
                appId={selectedApp ?? ""}
            />
        </ContainerWithCamkitIcon>
    );
};

const ButtonWrapper = styled(Flexbox)`
    gap: 16px;
`;

const dropdownStyle = css`
    width: 400px;
`;

const Title = styled.h2`
    ${Fonts.Heading.H300};
`;

const ErrorContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const ErrorMessage = styled.p`
    ${Fonts.Paragraph.P300};
    width: 400px;
    font-weight: 500;
    color: ${Colors.Red.V200};
`;

const authErrorCSS = css`
    white-space: nowrap;
`;

const viewRoleCss = css`
    text-align: center;
`;

const Required = styled.span`
    ${Fonts.Paragraph.P300};
    &:before {
        content: "* ";
        color: #c50a33;
    }
`;
